// Managers (their direct reports) and HR (everyone): dashboard, approvals and
// team attendance.
import React, { useState } from "react";
import { Button, TextField } from "@mui/material";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { fmtDate, fmtTime, fmtMinutes, days as daysText, thisMonth } from "./api";
import { useLoad, useAction, Loading, Card, Pill, Tag, Stat, Empty, Person, MonthNav, dayLabel } from "./shared";
import { LeaveCalendar } from "./Me";
import styles from "./Hrms.module.css";

const btn = { textTransform: "none", fontWeight: 700, fontFamily: "inherit", borderRadius: "10px", boxShadow: "none" };

function DecideButtons({ path, onDone }) {
  const [note, setNote] = useState("");
  const { busy, run } = useAction();
  const decide = (decision) => run(path, { method: "POST", body: { decision, note } }, onDone);
  return (
    <div className={styles.inline} style={{ justifyContent: "flex-end" }}>
      <TextField size="small" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ width: 170 }} />
      <Button size="small" variant="contained" color="success" startIcon={<CheckRoundedIcon />} disabled={busy} onClick={() => decide("approve")} sx={btn}>
        Approve
      </Button>
      <Button size="small" variant="outlined" color="error" startIcon={<CloseRoundedIcon />} disabled={busy} onClick={() => decide("reject")} sx={btn}>
        Reject
      </Button>
    </div>
  );
}

export function Approvals({ me, onChanged }) {
  const { data, error, reload } = useLoad("/approvals");
  const done = () => {
    reload();
    onChanged();
  };
  if (!data) return <Loading error={error} />;
  const tz = me.settings.timezone;
  return (
    <>
      <Card
        title="Leave requests"
        icon={<FactCheckOutlinedIcon />}
        color="blue"
        subtitle={me.isHr ? "Everyone's pending leave — HR can decide any request." : "Pending leave from people who report to you."}
        actions={data.leave.length ? <Tag color="blue">{data.leave.length} waiting</Tag> : null}
      >
        {data.leave.length === 0 ? (
          <Empty icon={<FactCheckOutlinedIcon />} title="All caught up" text="No leave waiting for a decision." />
        ) : (
          data.leave.map((r) => (
            <div key={r.id} className={`${styles.reqCard} ${styles.c_blue}`}>
              <Person name={r.employee?.name || "—"} sub={r.employee?.employee_code} size={40} />
              <div>
                <div className={styles.reqFacts}>
                  <span>
                    <b>{r.leave_type}</b>
                  </span>
                  <span>
                    {fmtDate(r.start_date)}
                    {r.end_date !== r.start_date ? ` – ${fmtDate(r.end_date)}` : ""}
                    {r.half_day ? ` (${r.half_day} half)` : ""}
                  </span>
                  <span>
                    <b>{daysText(r.days)}</b>
                  </span>
                  {r.balance_after != null && <span>{daysText(r.balance_after)} left after</span>}
                </div>
                {r.reason && <div className={styles.small} style={{ marginTop: 4 }}>“{r.reason}”</div>}
              </div>
              <DecideButtons path={`/leave/${r.id}/decide`} onDone={done} />
            </div>
          ))
        )}
      </Card>
      <Card
        title="Attendance fixes"
        icon={<EditCalendarOutlinedIcon />}
        color="amber"
        subtitle="Missed check-ins and check-outs people asked to correct."
        actions={data.regularizations.length ? <Tag color="amber">{data.regularizations.length} waiting</Tag> : null}
      >
        {data.regularizations.length === 0 ? (
          <Empty icon={<EditCalendarOutlinedIcon />} text="Nothing waiting." />
        ) : (
          data.regularizations.map((r) => (
            <div key={r.id} className={`${styles.reqCard} ${styles.c_amber}`}>
              <Person name={r.employee?.name || "—"} sub={fmtDate(r.date, { weekday: "short" })} size={40} />
              <div>
                <div className={styles.reqFacts}>
                  <span>
                    Asked for <b>{fmtTime(r.check_in, tz)} – {fmtTime(r.check_out, tz)}</b>
                  </span>
                </div>
                <div className={styles.small} style={{ marginTop: 4 }}>“{r.reason}”</div>
              </div>
              <DecideButtons path={`/regularizations/${r.id}/decide`} onDone={done} />
            </div>
          ))
        )}
      </Card>
    </>
  );
}

const numTone = (v, tone) => (v ? styles[`num_${tone}`] : undefined);

export function AttendanceTable({ rows }) {
  if (!rows.length) return <Empty icon={<GroupsOutlinedIcon />} text="No one to show." />;
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Employee</th>
            <th>Working days</th>
            <th>Present</th>
            <th>Late</th>
            <th>Half days</th>
            <th>Leave</th>
            <th>Absent</th>
            <th>No check-out</th>
            <th>Avg. day</th>
            <th>Overtime</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.employee_id}>
              <td>
                <Person name={r.name} sub={r.employee_code} size={30} />
              </td>
              <td>{r.working_days}</td>
              <td className={numTone(r.present, "good")}>{r.present}</td>
              <td className={numTone(r.late, "warn")}>{r.late || "—"}</td>
              <td className={numTone(r.half_day, "warn")}>{r.half_day || "—"}</td>
              <td className={numTone(r.leave, "info")}>{r.leave || "—"}</td>
              <td className={numTone(r.absent, "bad")}>{r.absent || "—"}</td>
              <td className={numTone(r.missing_checkout, "warn")}>{r.missing_checkout || "—"}</td>
              <td>{r.avg_minutes ? fmtMinutes(r.avg_minutes) : "—"}</td>
              <td>{r.overtime_minutes ? fmtMinutes(r.overtime_minutes) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Today's status for one person, as a pill.
function TodayPill({ day }) {
  if (!day) return <Pill status="cancelled" label="—" />;
  if (day.status === "future" || (day.status === "absent" && !day.check_in)) return <Pill status="cancelled" label="Not in yet" />;
  if (["not_employed", "not_tracked"].includes(day.status)) return <Pill status="cancelled" label="—" />;
  return <Pill status={day.status} label={dayLabel(day.status)} />;
}

// Split /team members by where they are today.
function bucketsOf(members) {
  const b = { in: [], leave: [], notIn: [], off: [] };
  for (const m of members) {
    const d = m.today || {};
    if (d.check_in) b.in.push(m);
    else if (["leave", "half_leave"].includes(d.status)) b.leave.push(m);
    else if (["holiday", "weekly_off"].includes(d.status)) b.off.push(m);
    else if (!["not_employed"].includes(d.status)) b.notIn.push(m);
  }
  return b;
}

function PeopleToday({ members, tz }) {
  return (
    <div className={styles.peopleGrid}>
      {members.map((m) => (
        <div key={m.id} className={styles.personCard}>
          <Person name={`${m.first_name} ${m.last_name || ""}`.trim()} sub={m.designation || m.employee_code} size={38} />
          <div className={styles.personCardFoot}>
            <TodayPill day={m.today} />
            <span className={styles.small}>
              {m.today && m.today.check_in ? `${fmtTime(m.today.check_in, tz)} – ${m.today.check_out ? fmtTime(m.today.check_out, tz) : "now"}` : ""}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// HR landing page: today at a glance and what needs attention.
export function HrHome({ me, goTo }) {
  const team = useLoad("/team");
  const approvals = useLoad("/approvals");
  if (!team.data) return <Loading error={team.error} />;
  const tz = me.settings.timezone;
  const members = team.data.members;
  const b = bucketsOf(members);
  const pending = approvals.data ? approvals.data.leave.length + approvals.data.regularizations.length : me.pendingApprovals;
  const nameOf = (m) => `${m.first_name} ${m.last_name || ""}`.trim();
  return (
    <>
      <div className={styles.tiles}>
        <Stat label="People" value={members.length} tone="violet" icon={<GroupsOutlinedIcon />} onClick={() => goTo("employees")} />
        <Stat label="In today" value={b.in.length} tone="good" icon={<HowToRegOutlinedIcon />} onClick={() => goTo("team")} />
        <Stat label="On leave" value={b.leave.length} tone="info" icon={<BeachAccessOutlinedIcon />} onClick={() => goTo("team")} />
        <Stat label="Not in yet" value={b.notIn.length} tone="warn" icon={<HourglassEmptyOutlinedIcon />} onClick={() => goTo("team")} />
        <Stat label="To approve" value={pending} tone={pending ? "bad" : "slate"} icon={<FactCheckOutlinedIcon />} onClick={() => goTo("approvals")} />
        {me.isHr && (
          <Stat label="HR requests" value={me.openHrRequests || 0} tone="teal" icon={<SupportAgentOutlinedIcon />} onClick={() => goTo("desk")} />
        )}
      </div>
      <div className={styles.grid2}>
        <Card
          title="Needs your decision"
          icon={<FactCheckOutlinedIcon />}
          color="red"
          actions={
            pending ? (
              <button type="button" className={styles.linkBtn} onClick={() => goTo("approvals")}>
                Review all →
              </button>
            ) : null
          }
        >
          {!approvals.data ? (
            <Loading error={approvals.error} />
          ) : pending === 0 ? (
            <Empty icon={<FactCheckOutlinedIcon />} title="All caught up" text="No leave or attendance fixes waiting." />
          ) : (
            [...approvals.data.leave.map((r) => ({ ...r, kind: "leave" })), ...approvals.data.regularizations.map((r) => ({ ...r, kind: "fix" }))]
              .slice(0, 6)
              .map((r) => (
                <div key={`${r.kind}${r.id}`} className={styles.listRow}>
                  <Person
                    name={r.employee?.name || "—"}
                    sub={
                      r.kind === "leave"
                        ? `${r.leave_type} · ${fmtDate(r.start_date)}${r.end_date !== r.start_date ? ` – ${fmtDate(r.end_date)}` : ""} · ${daysText(r.days)}`
                        : `Attendance fix · ${fmtDate(r.date)}`
                    }
                  />
                  <span style={{ marginLeft: "auto" }}>
                    <Tag color={r.kind === "leave" ? "blue" : "amber"}>{r.kind === "leave" ? "Leave" : "Fix"}</Tag>
                  </span>
                </div>
              ))
          )}
        </Card>
        <Card title="Where everyone is" icon={<TodayOutlinedIcon />} color="green" subtitle={fmtDate(team.data.today, { weekday: "long" })}>
          {[
            ["On leave", b.leave, "blue"],
            ["Not checked in yet", b.notIn, "amber"],
            ["In today", b.in, "green"],
          ].map(([label, list, color]) => (
            <div key={label} style={{ marginBottom: 12 }}>
              <div className={styles.inline} style={{ marginBottom: 6 }}>
                <Tag color={color}>{list.length}</Tag>
                <strong style={{ fontSize: 13.5 }}>{label}</strong>
              </div>
              {list.length ? (
                <div className={styles.inline}>
                  {list.map((m) => (
                    <span key={m.id} className={styles.heroChip} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", color: "#334155" }}>
                      {nameOf(m)}
                      {m.today && m.today.check_in ? ` · ${fmtTime(m.today.check_in, tz)}` : ""}
                    </span>
                  ))}
                </div>
              ) : (
                <div className={styles.small}>No one.</div>
              )}
            </div>
          ))}
          {b.off.length > 0 && <div className={styles.small}>{b.off.length} on a holiday / weekly off.</div>}
        </Card>
      </div>
      <LeaveCalendar me={me} title="Who's on leave" />
    </>
  );
}

export function TeamView({ me }) {
  const team = useLoad("/team");
  const [month, setMonth] = useState(thisMonth(me.today));
  const att = useLoad(`/team/attendance?month=${month}`);
  const tz = me.settings.timezone;
  const counts = team.data ? bucketsOf(team.data.members) : null;
  return (
    <>
      <Card
        title={me.isHr ? "Everyone today" : "My team today"}
        icon={<GroupsOutlinedIcon />}
        color="green"
        actions={
          counts ? (
            <div className={styles.inline}>
              <Tag color="green">{counts.in.length} in</Tag>
              <Tag color="blue">{counts.leave.length} on leave</Tag>
              <Tag color="amber">{counts.notIn.length} not in yet</Tag>
            </div>
          ) : null
        }
      >
        {!team.data ? (
          <Loading error={team.error} />
        ) : team.data.members.length === 0 ? (
          <Empty icon={<GroupsOutlinedIcon />} text="No one reports to you yet." />
        ) : (
          <PeopleToday members={team.data.members} tz={tz} />
        )}
      </Card>
      <Card title="Attendance" icon={<InsightsOutlinedIcon />} color="violet" actions={<MonthNav month={month} onChange={setMonth} />}>
        {att.data ? <AttendanceTable rows={att.data.rows} /> : <Loading error={att.error} />}
      </Card>
      <LeaveCalendar me={me} title="Who's on leave" />
    </>
  );
}
